import { useCallback, useEffect, useState } from 'react';
import {
  EntityFilteredType,
  StandardAndQuickEntityFilteredType,
} from 'utils/types/filters';

type FilterType<T> =
  | EntityFilteredType<T>
  | StandardAndQuickEntityFilteredType<T>;

type HookType<T> = {
  appliedFilters: FilterType<T> | {};
  canApplyFilters: boolean;
  handleUpdateFilters: (updatedFilters: FilterType<T>) => void;
  handleApplyFilters: () => void;
  handleClearAllFilters: () => void;
  handleClosePanel: () => void;
};

function useSidePanelFilters<T>(
  savedFilters: FilterType<T>,
  onUpdateFilters: (filters: FilterType<T> | {}) => void,
  onClosePanel: () => void
): HookType<T> {
  const [appliedFilters, setAppliedFilters] = useState<FilterType<T> | {}>({});
  const [canApplyFilters, setCanApplyFilters] = useState<boolean>(false);

  const setInitialFilters = useCallback(() => {
    if (savedFilters) {
      setAppliedFilters(savedFilters);
    }
  }, [savedFilters]);

  useEffect(() => {
    setInitialFilters();
  }, [setInitialFilters]);

  const handleUpdateFilters = (updatedFilters: FilterType<T>) => {
    setAppliedFilters(updatedFilters);
    setCanApplyFilters(true);
  };

  const handleApplyFilters = () => {
    onUpdateFilters(appliedFilters);
    setCanApplyFilters(false);
  };

  const handleClearAllFilters = () => {
    onUpdateFilters({});
    setAppliedFilters({});
    setCanApplyFilters(false);
  };

  const handleClosePanel = useCallback(() => {
    setInitialFilters();
    setCanApplyFilters(false);
    onClosePanel();
  }, [onClosePanel, setInitialFilters]);

  return {
    appliedFilters,
    canApplyFilters,
    handleUpdateFilters,
    handleApplyFilters,
    handleClearAllFilters,
    handleClosePanel,
  };
}

export default useSidePanelFilters;
